import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { useForm } from '@inertiajs/vue3';
    import EmployeeTabs from '@/components/employees/EmployeeTabs.vue';
    import { computed, ref } from 'vue';
    import DialogForm from '@/components/DialogForm.vue';
    import QueryKey from '@/enums/QueryKey';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData, Employee } from '@/types';
    import { useDateFormat } from '@/composables/useDateFormat';
    import axios from 'axios';
    import { today, getLocalTimeZone, startOfMonth } from '@internationalized/date';
    import { useQueryClient } from '@tanstack/vue-query';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'EditEmployee',
  props: {
    employee: {}
  },
  setup(__props: any) {

    const queryClient = useQueryClient();

    const props = __props;

    const { formatDateForRegex } = useDateFormat();
    const nextMonthStart = startOfMonth(today(getLocalTimeZone()).add({ months: 1 }));

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const tabs = ref<InstanceType<typeof EmployeeTabs>>();

    const getTypeValue = (fullString: string) => {
        const parts = fullString.split('\\');
        return parts[parts.length - 1];
    };

    const form = useForm({
        // Employment
        employment: {
            employment_type: getTypeValue(props.employee.employment_type),
            weekly_working_days: props.employee.employment.weekly_working_days,
            weekly_working_hours: props.employee.employment.weekly_working_hours,
            salary: props.employee.employment.salary,
            new_salary: '',
            new_salary_starts_at: nextMonthStart.toString(),
            deleted_future_salary_changes: [],
            hourly_rate: props.employee.employment.hourly_rate,
            tax_card: props.employee.employment.tax_card,

            has_free_car: !!props.employee.optional?.has_free_car,
            free_car_monthly: props.employee.employment.free_car_monthly,
            free_car_file: '',
            free_car_file_description: props.employee.employment.free_car_file_description,
            remove_free_car_file: false,

            kilometer: !!props.employee.optional?.kilometer,

            has_free_phone: !!props.employee.optional?.has_free_phone,

            has_lunch_arrangement: !!props.employee.optional?.has_lunch_arrangement,
            lunch_arrangement_monthly: props.employee.employment?.lunch_arrangement_monthly,

            has_fixed_add_ons: !!props.employee.optional?.has_fixed_add_ons,
            fixed_add_ons_monthly: props.employee.employment?.fixed_add_ons_monthly,

            has_pension: !!props.employee.optional?.has_pension,
            has_health_insurance: !!props.employee.optional?.health_insurance,

            danmarks_statistik: !!props.employee.optional?.danmarks_statistik,
            dst_salary_form: props.employee.employment?.dst_salary_form,
            dst_job_status: props.employee.employment?.dst_job_status,
            dst_hired_terms: props.employee.employment?.dst_hired_terms,
            disco_code: props.employee.employment?.disco_code,
            dst_time_limited_employment: props.employee.employment?.dst_time_limited_employment,
        },

        // Optional
        optional: {
            flex_savings: props.employee.optional?.flex_savings,
            days_vacation: props.employee.optional?.days_vacation,
            allocation_of_vacation_days: props.employee.optional?.allocation_of_vacation_days,
        },

        // Personal
        customer_employees: {
            id: props.employee.id,
            company_id: props.employee.company.id,
            first_name: props.employee.first_name,
            last_name: props.employee.last_name,
            cpr: '',
            email: props.employee.email,
            phone_cell: props.employee.phone_cell,
            department: props.employee.department,
            production_unit: '',
            employment_date: props.employee.employment_date,
        },

        // Address
        address: {
            main: {
                address: props.employee.addresses[0].address,
                address2: props.employee.addresses[0].address2,
                postal_code: props.employee.addresses[0].postal_code,
                city: props.employee.addresses[0].city,
                country_code: props.employee.addresses[0].country_code,
            },
        },

        // Bank
        bank: {
            bank_reg: props.employee.bank.bank_reg,
            bank_account: props.employee.bank.bank_account,
            iban: props.employee.bank.iban,
            swift: props.employee.bank.swift,
            use_iban: !!props.employee.bank.iban,
        },

        // Pension
        companyPension: {
            type: props.employee.company_pension?.type,
            employee_contribution: props.employee.company_pension?.employee_contribution ?? '',
            company_contribution: props.employee.company_pension?.company_contribution ?? '',
            pension_company: props.employee.company_pension?.pension_company ?? '',
        },

        // Credit cards
        credit_cards: props.employee.credit_card_ids,

        // User
        user: {
            id: props.employee.user_id,
            create_user: 0,
        },
    });

    const logEdit = () => {
        axios.get(route('v2.salary.employees.edit', { employeeId: props.employee.id }));
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DialogForm, {
    type: "update",
    form: _unref(form),
    route: _ctx.route('v2.salary.employees.update'),
    method: "post",
    "query-key": [_unref(QueryKey).EMPLOYEES, selectedCompanyId.value],
    title: "employee.edit.header",
    "success-message": "companies.save_success",
    "modal-size": "sm:max-w-xl",
    transform: 
            (data) => {
                Object.keys(data.optional).forEach((key) => {
                    if ([null, undefined, ''].includes(data.optional[key])) {
                        delete data.optional[key];
                    }
                });

                if (!data.employment.danmarks_statistik) {
                    [
                        'dst_salary_form',
                        'dst_job_status',
                        'dst_hired_terms',
                        'disco_code',
                        'dst_time_limited_employment',
                    ].forEach((field) => {
                        delete data.employment[field];
                    });
                }

                if (!data.employment.has_lunch_arrangement) {
                    delete data.employment.lunch_arrangement_monthly;
                }
                if (!data.employment.has_fixed_add_ons) {
                    delete data.employment.fixed_add_ons_monthly;
                }

                if (!data.employment.has_free_car) {
                    delete data.employment.free_car_monthly;
                    delete data.employment.free_car_file;
                    delete data.employment.free_car_file_description;
                }

                return {
                    ...data,
                    addresses: true,
                    customer_employees: {
                        ...data.customer_employees,
                        employment_date: data.customer_employees.employment_date
                            ? _unref(formatDateForRegex)(data.customer_employees.employment_date)
                            : '',
                    },
                    companyPension: {
                        ...data.companyPension,
                        type: data.companyPension.type ?? '',
                        employee_contribution: data.employment.has_pension
                            ? data.companyPension.employee_contribution
                            : '',
                        company_contribution: data.employment.has_pension
                            ? data.companyPension.company_contribution
                            : '',
                        pension_company: data.employment.has_pension
                            ? data.companyPension.pension_company
                            : '',
                    },
                };
            }
        ,
    onOpen: logEdit
  }, {
    default: _withCtx(() => [
      _createVNode(EmployeeTabs, {
        ref_key: "tabs",
        ref: tabs,
        form: _unref(form),
        employee: _ctx.employee
      }, null, 8, ["form", "employee"])
    ]),
    _: 1
  }, 8, ["form", "route", "query-key", "transform"]))
}
}

})